import {
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import {
  TablerAlertTriangle,
  TablerCircleCheck,
  TablerSettings,
} from '../../icons';
import SearchableTemplatePicker from './SearchableTemplatePicker';
import {
  RECAP_CARD_BOUNDARY_PROPS,
  RECAP_PRIMARY_BUTTON_PROPS,
  RECAP_SECONDARY_BUTTON_PROPS,
} from './recapButtonStyles';
import {
  PLANNER_MOTION_EASE,
  PLANNER_VIEW_ENTRANCE_DURATION_MS,
} from '../planner/plannerMotion';

const MotionBox = motion(Box);

const TemplateSelectionView = ({
  title,
  description,
  titleVariant = 'hero',
  templates = [],
  selectedId,
  onSelect,
  suggestedId,
  isLoadingSuggestion = false,
  isDisabled = false,
  isLoadingTemplates = false,
  loadingText = 'Loading templates...',
  accentColor,
  cardBg = 'white',
  borderColor = 'gray.200',
  subtleText = 'gray.500',
  maxH = '420px',
  showEhrNotice = false,
  ehrNoticeTitle = 'EHR-Integrated Templates',
  ehrNoticeDescription = "Your organization's templates will be used for note generation and EHR submission.",
  ehrLoadFailed = false,
  ehrLoadFailedTitle = "EHR templates couldn't be loaded",
  ehrLoadFailedDescription = "Your organization uses EHR-integrated templates, but we couldn't load them right now. Reload to try again.",
  onRetry,
  isRetrying = false,
  retryLabel = 'Reload templates',
  onManageTemplates,
  manageButtonLabel = 'Manage',
  actionLabel,
  onAction,
  isActionDisabled = false,
  isActionLoading = false,
  actionLoadingText,
  actionAriaLabel,
  actionLeftIcon,
  actionBg,
  actionHoverBg,
  actionActiveBg,
  listVariant = 'card',
  // clinicalNote-variant slots. `topContent` renders pinned at the top of the
  // card (a mode toggle lives there); `bodyOverride` swaps the header +
  // template picker for alternate content while the card chrome and the
  // single footer action stay put — so choosing what to generate never moves
  // the button the user is about to press. With a bodyOverride there is no
  // visible picker, so the footer no longer gates on a selected template;
  // `selectionSummaryLabel`/`selectionSummaryValue` let the caller relabel
  // the footer summary to match, and `secondaryAction` slots a quieter
  // affordance beside the primary action.
  topContent,
  bodyOverride,
  selectionSummaryLabel = 'Selected',
  selectionSummaryValue,
  secondaryAction,
  // When true (short vertical displays), the primary action is pinned
  // as a sticky footer instead of living below the full template list,
  // so it's always reachable without scrolling past every template. The
  // caller is responsible for ensuring no transformed/`will-change:
  // transform` ancestor sits between this view and the scroll container
  // (those break `position: sticky`).
  compact = false,
}: Record<string, any>) => {
  const prefersReducedMotion = useReducedMotion();
  const isHeroTitle = titleVariant === 'hero';
  const isPatientBrowserList = listVariant === 'patientBrowser';
  const isClinicalNoteList = listVariant === 'clinicalNote';
  const showHeader =
    Boolean(title) || Boolean(description) || Boolean(onManageTemplates);
  const selectedTemplate = selectedId
    ? templates.find((template: any) => template.id === selectedId)
    : null;
  const isClinicalActionDisabled =
    isActionDisabled || (!bodyOverride && !selectedTemplate);
  const summaryValue =
    selectionSummaryValue ?? (selectedTemplate?.name || 'No template selected');
  // `topContent` owns the card's top padding when present, so the body
  // beneath it tucks in closer.
  const clinicalTopPadding = topContent ? 4 : {base: 5, sm: 6};

  if (isClinicalNoteList) {
    const clinicalBody = bodyOverride ? (
      <Box px={{base: 5, sm: 6}} pt={clinicalTopPadding} pb={{base: 4, sm: 5}}>
        {bodyOverride}
      </Box>
    ) : (
      <>
        {showHeader ? (
          <Flex
            justify="space-between"
            align="flex-start"
            gap={4}
            px={{base: 5, sm: 6}}
            pt={clinicalTopPadding}
            pb={description ? 3 : 4}
          >
            <Box minW={0}>
              {title ? (
                <Text
                  as="h2"
                  fontWeight="semibold"
                  fontSize={
                    isHeroTitle
                      ? {base: 'xl', sm: '2xl'}
                      : {base: 'md', sm: 'lg'}
                  }
                  color="gray.900"
                  lineHeight="1.2"
                  letterSpacing="0"
                  mb={description ? 1 : 0}
                >
                  {title}
                </Text>
              ) : null}
              {description ? (
                <Text fontSize="sm" fontWeight="normal" color="gray.600">
                  {description}
                </Text>
              ) : null}
            </Box>

            {onManageTemplates ? (
              <Button
                {...RECAP_SECONDARY_BUTTON_PROPS}
                size="md"
                leftIcon={<TablerSettings size={14} />}
                onClick={onManageTemplates}
                aria-label="Manage templates"
                flexShrink={0}
              >
                {manageButtonLabel}
              </Button>
            ) : null}
          </Flex>
        ) : null}

        {ehrLoadFailed ? (
          <Box px={{base: 5, sm: 6}} pb={4}>
            <HStack
              spacing={3}
              px={{base: 3, sm: 4}}
              py={2.5}
              bg="gray.50"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.300"
              role="alert"
              aria-live="polite"
              align="center"
            >
              <Box
                as={TablerAlertTriangle}
                color="gray.600"
                boxSize={5}
                flexShrink={0}
                aria-hidden="true"
              />
              <Box minW={0} flex={1}>
                <Text
                  fontWeight="normal"
                  fontSize="sm"
                  color="gray.900"
                  lineHeight="shorter"
                >
                  {ehrLoadFailedTitle}
                </Text>
                <Text fontSize="xs" color="gray.900" mt={0.5} noOfLines={2}>
                  {ehrLoadFailedDescription}
                </Text>
              </Box>
              {onRetry ? (
                <Button
                  {...RECAP_SECONDARY_BUTTON_PROPS}
                  size="md"
                  onClick={onRetry}
                  isLoading={isRetrying}
                  loadingText="Reloading"
                  flexShrink={0}
                >
                  {retryLabel}
                </Button>
              ) : null}
            </HStack>
          </Box>
        ) : showEhrNotice ? (
          <Box px={{base: 5, sm: 6}} pb={4}>
            <HStack
              spacing={3}
              px={{base: 3, sm: 4}}
              py={2.5}
              bg="gray.50"
              borderRadius="lg"
              borderWidth="1px"
              borderColor="gray.300"
              align="center"
            >
              <Box
                as={TablerCircleCheck}
                color="gray.600"
                boxSize={5}
                flexShrink={0}
                aria-hidden="true"
              />
              <Text
                fontWeight="normal"
                fontSize="sm"
                color="gray.900"
                lineHeight="shorter"
                flex={1}
                minW={0}
                title={ehrNoticeDescription}
              >
                {ehrNoticeTitle}
                <Text
                  as="span"
                  fontWeight="normal"
                  color="gray.800"
                  display={{base: 'none', md: 'inline'}}
                  ml={2}
                >
                  · {ehrNoticeDescription}
                </Text>
              </Text>
            </HStack>
          </Box>
        ) : null}

        <Box px={{base: 5, sm: 6}} pb={{base: 4, sm: 5}}>
          {isLoadingTemplates ? (
            <Flex align="center" justify="center" py={8} direction="column">
              <Spinner color={accentColor} size="md" mb={3} thickness="2px" />
              <Text fontSize="sm" color={subtleText}>
                {loadingText}
              </Text>
            </Flex>
          ) : (
            <SearchableTemplatePicker
              templates={templates}
              selectedId={selectedId}
              onSelect={onSelect}
              suggestedId={suggestedId}
              isLoadingSuggestion={isLoadingSuggestion}
              isDisabled={isDisabled}
              accentColor={accentColor}
              cardBg={cardBg}
              borderColor={borderColor}
              subtleText={subtleText}
              maxH={maxH}
              listVariant={listVariant}
            />
          )}
        </Box>
      </>
    );

    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        overflow="hidden"
        textAlign="left"
        data-testid="template-selection-card"
      >
        {topContent ? (
          <Box px={{base: 5, sm: 6}} pt={{base: 5, sm: 6}}>
            {topContent}
          </Box>
        ) : null}
        {/* Only the body swaps when a bodyOverride toggles on/off; the card,
            the topContent toggle, and the footer action hold still. */}
        <AnimatePresence mode="wait" initial={false}>
          <MotionBox
            key={bodyOverride ? 'clinical-body-override' : 'clinical-body'}
            initial={prefersReducedMotion ? {opacity: 0} : {opacity: 0, y: 6}}
            animate={prefersReducedMotion ? {opacity: 1} : {opacity: 1, y: 0}}
            exit={prefersReducedMotion ? {opacity: 0} : {opacity: 0, y: -6}}
            transition={{
              duration: PLANNER_VIEW_ENTRANCE_DURATION_MS / 1000,
              ease: PLANNER_MOTION_EASE,
            }}
          >
            {clinicalBody}
          </MotionBox>
        </AnimatePresence>

        {actionLabel ? (
          <Flex
            px={{base: 5, sm: 6}}
            py={{base: 4, sm: 5}}
            borderTopWidth="1px"
            borderColor="gray.100"
            align={{base: 'stretch', sm: 'center'}}
            justify="space-between"
            gap={4}
            direction={{base: 'column', sm: 'row'}}
          >
            <Box minW={0} flex={1}>
              <Text
                color="gray.400"
                fontSize="sm"
                fontWeight="normal"
                lineHeight="1.2"
              >
                {selectionSummaryLabel}
              </Text>
              <Text
                color="gray.800"
                fontSize="md"
                fontWeight="medium"
                lineHeight="1.25"
                noOfLines={2}
                title={summaryValue || undefined}
              >
                {summaryValue}
              </Text>
            </Box>
            {secondaryAction ?? null}
            <Button
              {...RECAP_PRIMARY_BUTTON_PROPS}
              w={{base: '100%', sm: '240px'}}
              h="48px"
              minH="48px"
              size="md"
              bg={
                isClinicalActionDisabled ? 'gray.400' : actionBg || accentColor
              }
              color="white"
              fontSize="md"
              fontWeight="semibold"
              borderRadius="lg"
              _hover={{
                bg: isClinicalActionDisabled
                  ? 'gray.400'
                  : actionHoverBg || actionBg || accentColor,
              }}
              _active={{
                bg: actionActiveBg || actionHoverBg || actionBg || accentColor,
              }}
              leftIcon={actionLeftIcon}
              onClick={onAction}
              isDisabled={isClinicalActionDisabled}
              isLoading={isActionLoading}
              loadingText={actionLoadingText}
              aria-label={actionAriaLabel}
              flexShrink={0}
            >
              {actionLabel}
            </Button>
          </Flex>
        ) : null}
      </Box>
    );
  }

  return (
    <VStack spacing={compact ? 2 : 3} align="stretch" textAlign="left">
      {showHeader ? (
        <Flex justify="space-between" align="flex-start" gap={3}>
          <Box>
            {title ? (
              <Text
                fontWeight="normal"
                fontSize={isHeroTitle ? 'xl' : 'md'}
                letterSpacing={isHeroTitle ? '-0.02em' : 'normal'}
                color={accentColor}
                mb={description ? 0.5 : 0}
                lineHeight="shorter"
              >
                {title}
              </Text>
            ) : null}
            {description ? (
              <Text fontSize="sm" fontWeight="normal" color="gray.700">
                {description}
              </Text>
            ) : null}
          </Box>

          {onManageTemplates ? (
            <Button
              {...RECAP_SECONDARY_BUTTON_PROPS}
              size="md"
              leftIcon={<TablerSettings size={14} />}
              onClick={onManageTemplates}
              aria-label="Manage templates"
              flexShrink={0}
            >
              {manageButtonLabel}
            </Button>
          ) : null}
        </Flex>
      ) : null}

      {ehrLoadFailed ? (
        <HStack
          spacing={3}
          px={{base: 3, sm: 4}}
          py={2.5}
          bg="gray.50"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.300"
          role="alert"
          aria-live="polite"
          align="center"
        >
          <Box
            as={TablerAlertTriangle}
            color="gray.600"
            boxSize={5}
            flexShrink={0}
            aria-hidden="true"
          />
          <Box minW={0} flex={1}>
            <Text
              fontWeight="normal"
              fontSize="sm"
              color="gray.900"
              lineHeight="shorter"
            >
              {ehrLoadFailedTitle}
            </Text>
            <Text fontSize="xs" color="gray.900" mt={0.5} noOfLines={2}>
              {ehrLoadFailedDescription}
            </Text>
          </Box>
          {onRetry ? (
            <Button
              {...RECAP_SECONDARY_BUTTON_PROPS}
              size="md"
              onClick={onRetry}
              isLoading={isRetrying}
              loadingText="Reloading"
              flexShrink={0}
            >
              {retryLabel}
            </Button>
          ) : null}
        </HStack>
      ) : showEhrNotice ? (
        <HStack
          spacing={3}
          px={{base: 3, sm: 4}}
          py={2.5}
          bg="gray.50"
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.300"
          align="center"
        >
          <Box
            as={TablerCircleCheck}
            color="gray.600"
            boxSize={5}
            flexShrink={0}
            aria-hidden="true"
          />
          <Text
            fontWeight="normal"
            fontSize="sm"
            color="gray.900"
            lineHeight="shorter"
            flex={1}
            minW={0}
            title={ehrNoticeDescription}
          >
            {ehrNoticeTitle}
            <Text
              as="span"
              fontWeight="normal"
              color="gray.800"
              display={{base: 'none', md: 'inline'}}
              ml={2}
            >
              · {ehrNoticeDescription}
            </Text>
          </Text>
        </HStack>
      ) : null}

      <Box
        {...(isPatientBrowserList
          ? {}
          : {
              bg: cardBg,
              ...RECAP_CARD_BOUNDARY_PROPS,
              borderColor,
            })}
      >
        {isLoadingTemplates ? (
          <Flex align="center" justify="center" py={8} direction="column">
            <Spinner color={accentColor} size="md" mb={3} thickness="2px" />
            <Text fontSize="sm" color={subtleText}>
              {loadingText}
            </Text>
          </Flex>
        ) : (
          <SearchableTemplatePicker
            templates={templates}
            selectedId={selectedId}
            onSelect={onSelect}
            suggestedId={suggestedId}
            isLoadingSuggestion={isLoadingSuggestion}
            isDisabled={isDisabled}
            accentColor={accentColor}
            cardBg={cardBg}
            borderColor={borderColor}
            subtleText={subtleText}
            maxH={maxH}
            listVariant={listVariant}
          />
        )}
      </Box>

      {actionLabel ? (
        // On short displays the action is a sticky footer: the template
        // list scrolls beneath it and the primary "Create note" button
        // stays pinned to the bottom of the pane, always one tap away.
        // On tall displays it's a normal block at the end of the flow.
        <Box
          position={compact ? 'sticky' : undefined}
          bottom={compact ? 0 : undefined}
          zIndex={compact ? 2 : undefined}
          bg={compact ? cardBg : undefined}
          mt={compact ? 1 : 0}
          pt={compact ? 2 : 0}
          borderTopWidth={compact ? '1px' : 0}
          borderColor={borderColor}
        >
          <Button
            {...RECAP_PRIMARY_BUTTON_PROPS}
            w="100%"
            size="md"
            bg={isActionDisabled ? 'gray.400' : actionBg || accentColor}
            color="white"
            fontSize="md"
            _hover={{
              bg: isActionDisabled
                ? 'gray.400'
                : actionHoverBg || actionBg || accentColor,
            }}
            _active={{
              bg: actionActiveBg || actionHoverBg || actionBg || accentColor,
            }}
            leftIcon={actionLeftIcon}
            onClick={onAction}
            isDisabled={isActionDisabled}
            isLoading={isActionLoading}
            loadingText={actionLoadingText}
            aria-label={actionAriaLabel}
          >
            {actionLabel}
          </Button>
        </Box>
      ) : null}
    </VStack>
  );
};

export default TemplateSelectionView;
